import { Controller } from '@hotwired/stimulus'

// Filters the catalog reference picker by selected power, then submits the
// power form so the server resolves and pins a matching reference.
// Group-backed power options carry their group id in groupMapValue: the
// picker then shows every reference of that group, not just the
// representative kW. Groupless rows fall back to exact kW matching.
export default class extends Controller {
  static targets = ['power', 'picker']
  static values = { groupMap: Object }

  connect () {
    this.scheduleFilter()
  }

  filter () {
    this.applyFilter()
  }

  scheduleFilter () {
    if (this.applyFilter()) return

    // Parent controller connects before nested tom-select.
    window.requestAnimationFrame(() => this.applyFilter())
  }

  applyFilter () {
    const tom = this.#tomSelect()
    if (!tom) return false

    // Snapshot the full option list once, from the payload tom-select
    // already carries — no second JSON embed in the DOM.
    this.allOptions ||= Object.values(tom.options)

    const selected = this.powerTarget.value
    const selectedGroupId = this.groupMapValue[selected] || null
    const filtered = this.allOptions.filter((option) => {
      if (!option.value) return true
      if (!selected) return true

      return this.#powerMatches(option, selected, selectedGroupId)
    })

    const current = tom.getValue()
    const options = this.#withCurrentOption(filtered, current, tom)

    tom.clearOptions()
    tom.addOptions(options)
    if (current && options.some((option) => String(option.value) === String(current))) {
      tom.setValue(current, true)
    }

    return true
  }

  #tomSelect () {
    if (!this.hasPickerTarget || !this.hasPowerTarget) return null

    const controller = this.application.getControllerForElementAndIdentifier(
      this.pickerTarget,
      'tom-select'
    )
    return controller?.tomSelect || null
  }

  #powerMatches (option, power, selectedGroupId) {
    if (selectedGroupId) return String(option.group_id) === String(selectedGroupId)
    return Boolean(option.value) && String(option.power_kw) === String(power)
  }

  #withCurrentOption (filtered, current, tom) {
    if (!current) return filtered
    if (filtered.some((option) => String(option.value) === String(current))) {
      return filtered
    }

    const currentOption =
      tom.options[current] ||
      this.allOptions.find((option) => String(option.value) === String(current))

    return currentOption ? [...filtered, currentOption] : filtered
  }
}
